import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Star, ExternalLink } from "lucide-react";
import { RevealLines, Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Faq from "@/components/Faq";
import { BRAND, SERVICES, MANIFESTO, PROCESS, GALLERY, TESTIMONIALS, BEFORE_AFTER, IMAGES } from "@/lib/data";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#143D63]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroKitchen} alt="Luxury interior" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-[#0A0A0A]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#143D63]/70 via-transparent to-[#143D63]/30" />

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col justify-end pb-16 sm:pb-24">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="overline text-[#F8C21C] mb-6"
        >
          {BRAND.full} — Interior Design Studio
        </motion.p>

        <h1 className="font-serif font-light text-[#F9F8F6] tracking-tight text-[13vw] leading-[0.92] sm:text-[9vw] lg:text-[7.5rem]">
          <RevealLines lines={["Homes worth", "coming home to."]} delay={0.3} />
        </h1>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
            className="text-[#F9F8F6]/85 max-w-md text-base sm:text-lg"
          >
            Full-home interiors, modular kitchens & wardrobes — designed, engineered and installed by one accountable studio.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9 }}
          >
            <Link
              to="/consultation"
              data-testid="hero-book-btn"
              className="group inline-flex items-center gap-3 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm tracking-wide hover:bg-[#F9F8F6] transition-colors duration-300"
            >
              Book Free Consultation
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function MarqueeStrip() {
  const items = ["Full-Home Interiors", "Modular Kitchens", "Wardrobes", "Turnkey Commercial", "AI 2D + 360 Design", "On-Time Install"];
  return (
    <div data-testid="marquee" className="bg-[#8B0000] text-[#F9F8F6] py-6 relative z-10">
      <Marquee speed={45} gradient={false} autoFill>
        {items.map((t, i) => (
          <span key={i} className="font-serif text-3xl sm:text-4xl font-light mx-16 flex items-center gap-16">
            {t} <span className="text-[#F8C21C]">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Services() {
  return (
    <section data-testid="services" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-28 sm:py-36 relative z-10">
      <div className="grid lg:grid-cols-12 gap-6 items-end mb-16">
        <Reveal className="lg:col-span-8">
          <p className="overline text-[#8B0000] mb-4">What we do</p>
          <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[1]">
            Three ways we<br />transform your space
          </h2>
        </Reveal>
        <Reveal className="lg:col-span-4" delay={0.15}>
          <p className="text-[#143D63]/70">From a single statement kitchen to a fully finished home — every project is handled by one team, start to finish.</p>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.1}>
            <div className="group border border-[#143D63]/15 bg-white h-full">
              <div className="overflow-hidden aspect-[4/5]">
                <img src={s.img} alt={s.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
              </div>
              <div className="p-7">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-serif text-4xl text-[#F8C21C]">{s.no}</span>
                  <ArrowUpRight className="text-[#143D63] group-hover:text-[#8B0000] transition-colors" />
                </div>
                <h3 className="font-serif text-2xl text-[#143D63] mb-2">{s.title}</h3>
                <p className="text-sm text-[#143D63]/70 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section data-testid="manifesto" className="bg-[#143D63] text-[#F9F8F6] py-28 sm:py-36 relative z-10">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-[#F8C21C] mb-4">Our philosophy</p>
          <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight mb-20 max-w-2xl leading-[1]">
            Why people trust us with their homes
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-14">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.no} delay={(i % 2) * 0.1}>
              <div className="flex gap-6 border-t border-[#F9F8F6]/15 pt-8">
                <span className="font-serif text-6xl sm:text-7xl leading-none text-[#8B0000]">{m.no}</span>
                <div>
                  <h3 className="font-serif text-3xl mb-3">{m.title}</h3>
                  <p className="text-[#F9F8F6]/70 leading-relaxed">{m.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryPreview() {
  return (
    <section data-testid="gallery-preview" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-28 sm:py-36 relative z-10">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4">Selected work</p>
          <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[1]">The gallery</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/gallery" data-testid="gallery-view-all" className="group inline-flex items-center gap-2 text-[#143D63] font-medium link-underline">
            View all projects <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {GALLERY.slice(0, 4).map((g, i) => (
          <Reveal key={i} delay={(i % 2) * 0.1} className={g.span}>
            <Link to={`/projects/${g.slug}`} data-testid={`home-gallery-card-${g.slug}`} className="group relative overflow-hidden aspect-[16/10] block">
              <img src={g.img} alt={g.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-0 left-0 p-6 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                <p className="overline text-[#F8C21C] mb-1">{g.tag}</p>
                <h3 className="font-serif text-2xl text-[#F9F8F6]">{g.title}</h3>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function BeforeAfterShowcase() {
  return (
    <section data-testid="before-after-showcase" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-28 sm:py-36 relative z-10">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <p className="overline text-[#8B0000] mb-4">Before / After · Turnkey commercial</p>
            <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[1] max-w-3xl">
              Bare shells to brand-forward spaces.
            </h2>
          </div>
          <p className="text-[#143D63]/70 max-w-md text-sm">
            Every ZEBO commercial project starts as a raw shell and ends as a business-ready space — MEP, joinery, branding and finishes coordinated by one team. <b>Drag the slider</b> on any project below to see the transformation.
          </p>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-6">
        {BEFORE_AFTER.map((ba, i) => (
          <Reveal key={ba.slug} delay={(i % 2) * 0.08}>
            <div data-testid={`before-after-card-${ba.slug}`} className="border border-[#143D63]/15 bg-white group">
              <BeforeAfterSlider before={ba.before} after={ba.after} alt={ba.title} ariaId={`ba-${ba.slug}`} />
              <div className="p-6">
                <p className="overline text-[#8B0000] text-[10px]">{ba.category}</p>
                <h3 className="font-serif text-2xl text-[#143D63] mt-2 leading-tight">{ba.title}</h3>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-[#143D63]/65">
                  <span><b className="text-[#143D63]">Location</b> · {ba.location}</span>
                  <span><b className="text-[#143D63]">Delivered in</b> · {ba.duration}</span>
                  <span><b className="text-[#143D63]">Scope</b> · {ba.scope}</span>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 bg-[#143D63] text-[#F9F8F6] p-6 sm:p-8">
          <div>
            <p className="overline text-[#F8C21C] mb-1">Turnkey commercial interiors</p>
            <p className="font-serif text-2xl sm:text-3xl leading-tight">Open a brand-forward space in 30 – 90 days.</p>
          </div>
          <Link to="/commercial" data-testid="ba-commercial-btn" className="inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-5 py-3 font-bold text-sm hover:bg-[#F9F8F6] transition-colors">
            See commercial packages
          </Link>
        </div>
      </Reveal>
    </section>
  );
}


function AiTeaser() {
  return (
    <section data-testid="ai-teaser" className="relative z-10 overflow-hidden bg-[#0A0A0A] text-[#F9F8F6]">
      <div className="absolute inset-0 opacity-30">
        <img src={IMAGES.living2} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-28 sm:py-36 grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-7">
          <p className="overline text-[#F8C21C] mb-4">ZEBO AI Studio</p>
          <h2 className="font-serif text-5xl sm:text-7xl font-light tracking-tight leading-[0.98] mb-6">
            See your dream<br />room in seconds
          </h2>
          <p className="text-[#F9F8F6]/75 max-w-lg mb-8">
            Describe a room, pick a style, and our AI renders a photorealistic design instantly. The perfect starting point before your consultation.
          </p>
          <Link
            to="/ai-studio"
            data-testid="ai-teaser-btn"
            className="group inline-flex items-center gap-3 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm tracking-wide hover:bg-[#F9F8F6] transition-colors duration-300"
          >
            Try the AI Room Generator
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.15}>
          <div className="border border-[#F9F8F6]/20 overflow-hidden aspect-[4/5]">
            <img src={IMAGES.wardrobe1} alt="AI designed room" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function StudioStrip() {
  return (
    <section data-testid="studio-strip" className="relative z-10 bg-[#143D63] text-[#F9F8F6] border-t border-b border-[#F8C21C]/25">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 sm:py-16 grid md:grid-cols-12 gap-8 items-center">
        <Reveal className="md:col-span-8">
          <p className="overline text-[#F8C21C] mb-3">Free AI portal · Now live</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05]">
            Upload your floor plan.<br className="hidden sm:block" /> <span className="text-[#F8C21C]">Generate 3D, wardrobe & room ideas in seconds.</span>
          </h2>
          <p className="text-[#F9F8F6]/75 mt-5 max-w-2xl text-sm sm:text-base leading-relaxed">
            Sign up free at <b>My Studio</b>, upload a floor plan, a bare wall or an existing room — our AI will redesign it in your chosen style, with an optional 360° walkthrough. No credit card. Unlimited generations.
          </p>
        </Reveal>
        <Reveal className="md:col-span-4 flex md:justify-end" delay={0.15}>
          <Link
            to="/studio"
            data-testid="studio-strip-cta"
            className="group inline-flex items-center gap-3 bg-[#F8C21C] text-[#143D63] px-7 py-4 font-bold text-sm hover:bg-[#F9F8F6] transition-colors duration-300"
          >
            Open My Studio
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section data-testid="process" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-28 sm:py-36 relative z-10">
      <Reveal>
        <p className="overline text-[#8B0000] mb-4">How it works</p>
        <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] mb-16 leading-[1]">
          From idea to move-in
        </h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#143D63]/15 border border-[#143D63]/15">
        {PROCESS.map((p, i) => (
          <Reveal key={p.no} delay={i * 0.08} className="bg-[#F9F8F6]">
            <div className="p-8 h-full">
              <span className="font-serif text-5xl text-[#F8C21C]">{p.no}</span>
              <h3 className="font-serif text-2xl text-[#143D63] mt-4 mb-2">{p.title}</h3>
              <p className="text-sm text-[#143D63]/70">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section data-testid="testimonials" className="bg-[#143D63] text-[#F9F8F6] py-28 sm:py-36 relative z-10">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <p className="overline text-[#F8C21C] mb-4">Loved by homeowners</p>
              <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight leading-[1]">Kind words</h2>
            </div>
            <a
              href={BRAND.reviewUrl}
              target="_blank"
              rel="nofollow noopener noreferrer external"
              data-testid="read-google-reviews"
              className="inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-5 py-3 text-xs font-bold hover:bg-[#F9F8F6] transition-colors"
            >
              <ExternalLink size={13} /> Read reviews on Google
            </a>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={(i % 3) * 0.1}>
              <div data-testid={`testimonial-${i}`} className="border border-[#F9F8F6]/15 p-8 h-full flex flex-col hover:border-[#F8C21C]/60 transition-colors">
                <div className="flex items-center gap-1 mb-5">
                  {[...Array(t.rating || 5)].map((_, s) => (
                    <Star key={s} size={13} className="text-[#F8C21C] fill-[#F8C21C]" />
                  ))}
                </div>
                <p className="font-serif text-xl leading-snug text-[#F9F8F6] mb-6">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-auto">
                  <p className="font-bold text-[#F8C21C]">{t.name}</p>
                  <p className="text-sm text-[#F9F8F6]/60">{t.place}</p>
                  {t.project && <p className="text-[11px] text-[#F9F8F6]/40 mt-1 uppercase tracking-wider">{t.project}</p>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocalSeo() {
  const regions = [
    { name: "West Bengal", cities: ["Kolkata (HQ)", "Howrah", "Durgapur", "Asansol", "Siliguri", "Kalyani", "Kharagpur"] },
    { name: "Jharkhand",  cities: ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro"] },
    { name: "Odisha",     cities: ["Bhubaneswar", "Cuttack", "Rourkela", "Puri"] },
    { name: "Bihar",      cities: ["Patna", "Gaya", "Muzaffarpur", "Bhagalpur"] },
  ];
  const services = [
    { h: "Best interior designers in Kolkata & eastern India", p: "From concept to handover, ZEBO delivers full-home interior design across West Bengal, Jharkhand, Odisha and Bihar — thoughtfully planned living rooms, bedrooms and puja rooms tailored to how your family lives." },
    { h: "Modular kitchen design", p: "Factory-precise modular kitchens with premium finishes, soft-close hardware and smart storage — engineered for the humidity and lifestyles of eastern India and built to last." },
    { h: "Wardrobes & storage", p: "Bespoke sliding and hinged wardrobes, walk-in closets and space-saving storage designed around your rooms and your things — installed from Kolkata to Ranchi to Bhubaneswar." },
    { h: "Turnkey commercial interiors", p: "Offices, restaurants, cafés, salons and retail — brand-forward commercial interior design delivered end-to-end across eastern India, on time and on budget." },
  ];
  return (
    <section data-testid="local-seo" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-28 sm:py-36 relative z-10">
      <Reveal>
        <p className="overline text-[#8B0000] mb-4">Serving eastern India</p>
        <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[1] max-w-3xl">
          Interior design across West Bengal, Jharkhand, Odisha &amp; Bihar
        </h2>
        <p className="text-[#143D63]/70 mt-6 max-w-2xl text-base sm:text-lg">
          Looking for the best interior design company in eastern India? ZEBO India Private Limited specialises in luxury full-home
          interiors, modular kitchens, custom wardrobes and <b>turnkey commercial interior design</b> for offices,
          restaurants, cafés and retail — from our Chambers Mall studio in Kolkata to sites across Ranchi, Jamshedpur, Bhubaneswar, Patna, Durgapur, Siliguri and beyond.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#143D63]/15 border border-[#143D63]/15 mt-14">
        {services.map((s, i) => (
          <Reveal key={s.h} delay={i * 0.08} className="bg-[#F9F8F6]">
            <div className="p-7 h-full">
              <h3 className="font-serif text-2xl text-[#143D63] mb-3">{s.h}</h3>
              <p className="text-sm text-[#143D63]/70 leading-relaxed">{s.p}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-14" data-testid="area-served-grid">
          <p className="overline text-[#143D63]/60 mb-4">Areas we serve</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#143D63]/15 border border-[#143D63]/15">
            {regions.map((r) => (
              <div key={r.name} className="bg-[#F9F8F6] p-6">
                <p className="font-serif text-xl text-[#8B0000] mb-3">{r.name}</p>
                <div className="flex flex-wrap gap-1.5">
                  {r.cities.map((c) => (
                    <span key={c} className="text-[11px] border border-[#143D63]/20 px-2 py-1 text-[#143D63]/80 bg-white">{c}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#143D63]/50 mt-4">Complimentary site visits within 25 km of our Kolkata studio. Rest of eastern India served with logged design + install visits scheduled around each project milestone.</p>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  const faqItems = [
    {
      q: "How much does a modular kitchen cost in Kolkata?",
      a: "For a standard 3BHK, ZEBO modular kitchens typically range ₹1.8 L to ₹6.5 L depending on the layout (L-shape, U-shape or Island), core material (BWR plywood vs HDHMR vs marine ply) and finish (laminate, acrylic, PU lacquer or wood veneer). We publish a live BOM in our 3D configurator so you can price your exact design in real time.",
    },
    {
      q: "What's included in a 2 BHK / 3 BHK / 4 BHK full-home interior package?",
      a: "Every package covers: modular kitchen, all wardrobes and storage, false ceiling, lighting, wall décor, TV & entertainment units, dining, study/home-office joinery, painting, plus complete civil, electrical and plumbing coordination. Indicative starting points: 2BHK ₹6.5–14 L · 3BHK ₹10–22 L · 4BHK ₹16–35 L. Final scope and BOQ are locked after a free site visit.",
    },
    {
      q: "How long does a full home interior project take end-to-end?",
      a: "A 2BHK is usually 45–60 days from BOQ sign-off to handover; a 3BHK 60–75 days; a 4BHK 75–100 days. Factory-manufactured modular pieces run in parallel with on-site civil work, which is how we hit tight timelines without cutting corners. You get a Gantt chart in your Client Dashboard on day one.",
    },
    {
      q: "Do you handle turnkey commercial interiors — office, restaurant, café, retail?",
      a: "Yes. ZEBO delivers complete turnkey commercial fit-outs across Kolkata for offices (workstations, cabins, meeting rooms), restaurants and cafés (kitchen equipment coordination, seating, POS/bar, branded wall), salons and clinics, and retail/showroom spaces. Typical timelines: café/salon 30–45 days, mid-size office 45–75 days, restaurant 60–90 days.",
    },
    {
      q: "What warranty does ZEBO offer on modular kitchens and wardrobes?",
      a: "10-year warranty on modular carcasses, 5 years on hardware (Hettich / Blum / Ebco depending on the tier), and 1-year comprehensive service on installation. All materials carry ISI-marked plywood grades and factory-tested finishes, so joints, hinges and channels are covered.",
    },
    {
      q: "Which areas does ZEBO India serve?",
      a: "ZEBO delivers projects across four eastern-Indian states — West Bengal (Kolkata, Howrah, Durgapur, Asansol, Siliguri, Kharagpur), Jharkhand (Ranchi, Jamshedpur, Dhanbad, Bokaro), Odisha (Bhubaneswar, Cuttack, Rourkela, Puri) and Bihar (Patna, Gaya, Muzaffarpur, Bhagalpur). Site visits within 25 km of our Kolkata Chambers Mall studio are complimentary; sites further afield are served with scheduled visits at each project milestone.",
    },
    {
      q: "Can I see my design in 3D or 360° before signing?",
      a: "Yes — every ZEBO project ships with a photorealistic 2D render, a walk-through 360° panorama, and, for kitchens, a live 3D configurator you can drag and rotate yourself. You can also snap a photo of your existing room in AI Studio and our AI will preview a makeover on the exact space.",
    },
    {
      q: "Is the design consultation free?",
      a: "Yes. The first consultation — site visit, requirement mapping, mood board and rough budget — is completely free with zero obligation. We only charge when you sign off the detailed BOQ.",
    },
  ];

  return (
    <main>
      <Seo
        title="Best Interior Designers in Kolkata | Home + Turnkey Commercial Interiors — ZEBO"
        description="ZEBO India Private Limited — Kolkata's premier interior design company for full-home interiors, modular kitchens, wardrobes and turnkey commercial spaces (office, restaurant, café, retail, salon). Book a free consultation."
        path="/"
      />
      <Hero />
      <MarqueeStrip />
      <Services />
      <Manifesto />
      <GalleryPreview />
      <BeforeAfterShowcase />
      <AiTeaser />
      <StudioStrip />
      <Process />
      <Testimonials />
      <LocalSeo />
      <Faq items={faqItems} />
    </main>
  );
}
